import { useState } from 'react';
import { useHomepageSection } from '@/hooks/useHomepageSection';
import { TESTIMONIALS_COPY } from '@/content/yawasCopy';

interface Testimonial {
  name: string;
  role: string;
  text: string;
  rating: number;
  avatar: string;
}

const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    name: 'Markus B.',
    role: 'Stammkunde seit 2021',
    text: 'Die Smash Burger bei Yawas Kebab sind einfach hervorragend – saftig, frisch und mit echtem Geschmack. Hier merkt man die Leidenschaft hinter jedem Gericht.',
    rating: 5,
    avatar: '',
  },
  {
    name: 'Sandra K.',
    role: 'Gästin aus St. Gallen',
    text: 'Freundlicher Service, frische Zutaten und ein Döner, der begeistert. Yawas Kebab ist unser Lieblingsimbiss in St. Gallen – absolut empfehlenswert.',
    rating: 5,
    avatar: '',
  },
  {
    name: 'Thomas R.',
    role: 'Treuer Gast',
    text: 'Hausgemachte Patties, ehrliche Qualität und herzliche Gastfreundschaft. Seit der Eröffnung 2021 kommen wir regelmässig – und jedes Mal mit Freude.',
    rating: 5,
    avatar: '',
  },
];

const TestimonialsSection = () => {
  const [active, setActive] = useState(0);
  const { data } = useHomepageSection('testimonials');

  const title = data?.title?.trim() || TESTIMONIALS_COPY.title;
  const subtitle = data?.subtitle?.trim() || TESTIMONIALS_COPY.subtitle;

  let testimonials: Testimonial[] = DEFAULT_TESTIMONIALS;
  if (data?.extra_json) {
    try {
      const parsed = data.extra_json as Testimonial[];
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.text) {
        testimonials = parsed;
      }
    } catch {
      testimonials = DEFAULT_TESTIMONIALS;
    }
  }

  const safeActive = Math.min(active, testimonials.length - 1);
  const t = testimonials[safeActive] ?? testimonials[0];

  return (
    <section className="py-20 md:py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">{subtitle}</span>
          <h2
            className="text-3xl md:text-5xl font-bold text-stone-900 mt-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-stone-900 rounded-3xl overflow-hidden">
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="text-amber-400 text-5xl font-serif leading-none mb-4">&ldquo;</div>
              <p className="text-white text-base md:text-lg leading-relaxed mb-6">{t.text}</p>
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: Math.min(t.rating, 5) }).map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{t.name}</div>
                <div className="text-stone-400 text-xs mt-0.5">{t.role}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`transition-all duration-200 rounded-full cursor-pointer ${
                  i === safeActive ? 'w-8 h-2.5 bg-amber-500' : 'w-2.5 h-2.5 bg-stone-200 hover:bg-stone-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
